import { forwardRef, ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { Prompt, useHistory, useLocation } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import {
  ComponentsContextProvider,
  LinkComponent,
  PromptComponent,
  To,
} from '@rossum/components/ComponentsContext';
import { Link as MuiLink } from '@rossum/ui/material';

// forwardRef so the ref MUI attaches (e.g. Tooltip's popper anchor, when a Chip
// or Button renders as this link) reaches the underlying anchor element.
const LinkAdapter: LinkComponent = forwardRef(({ to, ...rest }, ref) => (
  <MuiLink ref={ref} component={RouterLink} to={to} {...rest} />
));

const PromptAdapter: PromptComponent = ({ when, message }) => {
  const history = useHistory();
  return (
    <Prompt
      when={when}
      message={nextLocation => {
        // Same-pathname navigations don't need to be intercepted.
        if (nextLocation.pathname === history.location.pathname) {
          return true;
        }
        if (typeof message === 'string') {
          return message;
        }
        const target: To = {
          pathname: nextLocation.pathname,
          search: nextLocation.search,
          hash: nextLocation.hash,
          state: nextLocation.state,
        };
        return message(target);
      }}
    />
  );
};

const useRouteKey = () => useLocation().pathname;

export const ComponentsProvider = ({ children }: { children: ReactNode }) => {
  const intl = useIntl();
  return (
    <ComponentsContextProvider
      LinkComponent={LinkAdapter}
      PromptComponent={PromptAdapter}
      useRouteKey={useRouteKey}
      intl={intl}
    >
      {children}
    </ComponentsContextProvider>
  );
};
